/**
 * Reiter „Vorgänge": die Entscheidungsliste aus dem Nachtlauf, als nummerierte
 * Karten mit Ja / Später / Nein und dem vorbereiteten Artefakt (Mailentwurf,
 * Änderung, Leitfaden) zum Aufklappen. Muster der Morgenrunde im dunklen Thema.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { listVorgaenge, entscheideVorgang, zahl, PRIO_FARBE, type Vorgang, type VorgangStatus, type Artefakt, type MailEntwurf, type Aenderung, type Leitfaden, type Datei, type Auftrag } from '@/lib/leitwarte-api';
import { Laden, Fehlerzeile, Leer, T, kurzDatum, plusTage, kapital, mehrzahl } from './bausteine';

type Filter = 'offen' | 'spaeter' | 'freigegeben' | 'erledigt' | 'alle';
type Entscheidung = 'ja' | 'spaeter' | 'nein' | 'erledigt';

const FILTER: Array<{ wert: Filter; label: string }> = [
  { wert: 'offen', label: 'Offen' },
  { wert: 'spaeter', label: 'Später' },
  { wert: 'freigegeben', label: 'Freigegeben' },
  { wert: 'erledigt', label: 'Erledigt' },
  { wert: 'alle', label: 'Alle' },
];

const STATUS_WORT: Record<VorgangStatus, string> = {
  offen: 'offen',
  freigegeben: 'freigegeben',
  spaeter: 'auf Wiedervorlage',
  erledigt: 'erledigt',
  verworfen: 'verworfen',
  in_arbeit: 'in Arbeit',
};

const quelleLabel = (q: string) => kapital((q ?? '').replace(/[_-]+/g, ' '));

function artefaktLink(a: Artefakt, offen: boolean): string {
  const text = a.art === 'mail_entwurf' ? 'Entwurf lesen' : a.art === 'aenderung' ? 'Änderung ansehen' : a.art === 'leitfaden' ? 'Leitfaden öffnen' : a.art === 'datei' ? 'Dateien ansehen' : a.art === 'auftrag' ? 'Auftrag ansehen' : 'Details';
  return offen ? 'Zuklappen' : text;
}

function meldung(e: Entscheidung, datum?: string): string {
  if (e === 'ja') return 'Freigegeben';
  if (e === 'spaeter') return `Wiedervorlage am ${kurzDatum(datum)}`;
  if (e === 'nein') return 'Verworfen';
  return 'Erledigt';
}

async function kopieren(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Text kopiert');
  } catch {
    toast.error('Kopieren nicht möglich');
  }
}

function ArtefaktPanel({ a }: { a: Artefakt }) {
  if (a.art === 'mail_entwurf') {
    const m = a as MailEntwurf;
    return (
      <div className="lw-innen lw-eingerueckt" style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
        <div className="lw-zeile" style={{ gap: 8 }}>
          <span className="lw-ged" style={{ width: 52 }}>An</span>
          <span className="lw-sek" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.an}</span>
          <button type="button" className="lw-knopf lw-knopf-klein" style={{ marginLeft: 'auto' }} onClick={() => kopieren(m.text ?? '')}>
            <Copy size={12} /> Text kopieren
          </button>
        </div>
        <div className="lw-zeile" style={{ gap: 8 }}>
          <span className="lw-ged" style={{ width: 52 }}>Betreff</span>
          <span className="lw-weiss" style={{ fontWeight: 600 }}>{m.betreff}</span>
        </div>
        <pre className="lw-pre" style={{ borderTop: `1px solid ${T.rand}`, paddingTop: 6 }}>{m.text}</pre>
      </div>
    );
  }
  if (a.art === 'aenderung') {
    const ae = a as Aenderung;
    return (
      <div className="lw-innen lw-eingerueckt" style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
        <div className="lw-zeile" style={{ gap: 8 }}>
          <span className="lw-chip">{ae.system}</span>
          <span className="lw-sek">{ae.beschreibung}</span>
        </div>
        {ae.payload ? <pre className="lw-code" style={{ borderTop: `1px solid ${T.rand}`, paddingTop: 6 }}>{JSON.stringify(ae.payload, null, 2)}</pre> : null}
      </div>
    );
  }
  if (a.art === 'leitfaden') {
    const l = a as Leitfaden;
    return (
      <div className="lw-innen lw-eingerueckt" style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
        <span className="lw-weiss" style={{ fontWeight: 700 }}>{l.titel}</span>
        <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4, color: T.sek, lineHeight: 1.5 }}>
          {(l.punkte ?? []).map((p, i) => <li key={i}>{p}</li>)}
        </ol>
      </div>
    );
  }
  if (a.art === 'datei') {
    const d = a as Datei;
    return (
      <div className="lw-innen lw-eingerueckt" style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
        {(d.dateien ?? []).map((f, i) => (
          <div key={i} className="lw-zeile" style={{ gap: 8 }}>
            <span className="lw-sek" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.beschreibung ? `${f.beschreibung}: ` : ''}<span className="lw-weiss">{f.pfad}</span></span>
            <button type="button" className="lw-knopf lw-knopf-klein" style={{ marginLeft: 'auto' }} onClick={() => kopieren(f.pfad)}><Copy size={12} /> Pfad kopieren</button>
          </div>
        ))}
        {d.hinweis ? <span className="lw-ged">{d.hinweis}</span> : null}
      </div>
    );
  }
  if (a.art === 'auftrag') {
    const au = a as Auftrag;
    return (
      <div className="lw-innen lw-eingerueckt" style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
        <div className="lw-zeile" style={{ gap: 8 }}>
          <span className="lw-chip">Skill {au.skill}</span>
          {au.kunde ? <span className="lw-sek">{au.kunde}</span> : null}
        </div>
        <pre className="lw-pre">{au.auftrag}</pre>
        {au.bericht ? <pre className="lw-pre" style={{ borderTop: `1px solid ${T.rand}`, paddingTop: 6 }}>{au.bericht}</pre> : <span className="lw-ged">Noch nicht ausgeführt. Der Auftragslauf holt sich freigegebene Aufträge alle 30 Minuten, solange die Claude-App offen ist.</span>}
        {(au.dateien ?? []).map((f, i) => (
          <div key={i} className="lw-zeile" style={{ gap: 8 }}>
            <span className="lw-sek" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.beschreibung ? `${f.beschreibung}: ` : ''}<span className="lw-weiss">{f.pfad}</span></span>
            <button type="button" className="lw-knopf lw-knopf-klein" style={{ marginLeft: 'auto' }} onClick={() => kopieren(f.pfad)}><Copy size={12} /> Pfad kopieren</button>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="lw-innen lw-eingerueckt">
      <pre className="lw-code">{JSON.stringify(a, null, 2)}</pre>
    </div>
  );
}

function VorgangKarte({ v, nr, fokus, aufgeklappt, umklappen, entscheiden, laeuft }: {
  v: Vorgang; nr: number; fokus: boolean; aufgeklappt: boolean; umklappen: () => void;
  entscheiden: (e: Entscheidung, wiedervorlage_am?: string) => void; laeuft: boolean;
}) {
  const [spaeterOffen, setSpaeterOffen] = useState(false);
  const [datum, setDatum] = useState(plusTage(3));
  const knoepfe = v.knoepfe && v.knoepfe.length ? v.knoepfe : [];
  const jaText = knoepfe[0] || 'Ja';
  const spaeterText = knoepfe[1] || 'Später';
  const neinText = knoepfe[2] || 'Nein';
  const entscheidbar = v.status === 'offen' || v.status === 'spaeter' || v.status === 'in_arbeit';

  return (
    <article id={`vorgang-${v.id}`} className={`lw-vorgang${fokus ? ' lw-vorgang-fokus' : ''}`} style={{ borderLeftColor: PRIO_FARBE[v.prioritaet] ?? T.blau }}>
      <div className="lw-zeile">
        <span className="lw-nummer">{nr}</span>
        <span style={{ fontSize: 14, fontWeight: 600, minWidth: 0 }}>{v.titel}</span>
        <span className="lw-chip" style={{ marginLeft: 'auto' }}>{STATUS_WORT[v.status] ?? v.status}</span>
      </div>
      {v.beschreibung ? <p className="lw-pre lw-eingerueckt">{v.beschreibung}</p> : null}
      {v.vorschlag ? (
        <p className="lw-eingerueckt" style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: T.text }}>
          <span className="lw-ged">Mein Vorschlag:</span> {v.vorschlag}
        </p>
      ) : null}
      <div className="lw-eingerueckt" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
        {v.kunde_name ? <span className="lw-chip">{v.kunde_name}</span> : null}
        {v.quelle ? <span className="lw-chip lw-ged">{quelleLabel(v.quelle)}</span> : null}
        {v.faellig_am ? <span className="lw-chip">fällig {kurzDatum(v.faellig_am)}</span> : null}
        {v.status === 'spaeter' && v.wiedervorlage_am ? <span className="lw-chip">Wiedervorlage {kurzDatum(v.wiedervorlage_am)}</span> : null}
        {v.autopilot === 'selbst' ? <span className="lw-chip lw-ged">macht der Lauf selbst</span> : null}
        {v.artefakt ? (
          <button type="button" className="lw-link" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={umklappen}>
            {artefaktLink(v.artefakt, aufgeklappt)}
            {aufgeklappt ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        ) : null}
      </div>
      {entscheidbar || v.status === 'freigegeben' ? (
        <div className="lw-eingerueckt" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {entscheidbar ? (
            <>
              <button type="button" className="lw-knopf lw-knopf-primaer" disabled={laeuft} onClick={() => entscheiden('ja')}>{jaText}</button>
              <button type="button" className="lw-knopf" disabled={laeuft} onClick={() => setSpaeterOffen((s) => !s)}>{spaeterText}</button>
              <button type="button" className="lw-knopf lw-knopf-leise" disabled={laeuft} onClick={() => entscheiden('nein')}>{neinText}</button>
            </>
          ) : null}
          {v.status === 'freigegeben' ? (
            <button type="button" className="lw-knopf lw-knopf-primaer" disabled={laeuft} onClick={() => entscheiden('erledigt')}>Erledigt</button>
          ) : null}
          {spaeterOffen ? (
            <span className="lw-zeile" style={{ gap: 6 }}>
              <input type="date" className="lw-eingabe" style={{ height: 32 }} value={datum} min={plusTage(1)} onChange={(e) => setDatum(e.target.value)} aria-label="Wiedervorlage am" />
              <button type="button" className="lw-knopf" disabled={laeuft || !datum} onClick={() => { entscheiden('spaeter', datum); setSpaeterOffen(false); }}>Wiedervorlage setzen</button>
              <button type="button" className="lw-link lw-ged" onClick={() => setSpaeterOffen(false)}>Abbrechen</button>
            </span>
          ) : null}
        </div>
      ) : null}
      {aufgeklappt && v.artefakt ? <ArtefaktPanel a={v.artefakt} /> : null}
    </article>
  );
}

export default function Vorgaenge({ fokusId }: { fokusId: string | null }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('offen');
  const [aufgeklappt, setAufgeklappt] = useState<string | null>(fokusId);
  const umgeschaltet = useRef<string | null>(null);
  const gescrollt = useRef<string | null>(null);

  const q = useQuery({ queryKey: ['leitwarte', 'vorgaenge', filter], queryFn: () => listVorgaenge(filter) });
  const liste = q.data ?? [];

  // Fokus aus der URL (?id=): erst im aktuellen Filter suchen, sonst einmal auf „Alle" wechseln, dann hinscrollen.
  useEffect(() => {
    if (!fokusId || !q.data) return;
    const drin = q.data.some((v) => v.id === fokusId);
    if (!drin) {
      if (filter !== 'alle' && umgeschaltet.current !== fokusId) {
        umgeschaltet.current = fokusId;
        setFilter('alle');
      }
      return;
    }
    if (gescrollt.current === fokusId) return;
    gescrollt.current = fokusId;
    setAufgeklappt(fokusId);
    window.setTimeout(() => document.getElementById(`vorgang-${fokusId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
  }, [fokusId, q.data, filter]);

  const mut = useMutation({
    mutationFn: (p: { id: string; entscheidung: Entscheidung; wiedervorlage_am?: string }) => entscheideVorgang(p.id, p.entscheidung, undefined, p.wiedervorlage_am),
    onMutate: async (p) => {
      // Optimistisch: die Karte verlässt die gefilterte Liste sofort, „Alle" behält sie.
      await qc.cancelQueries({ queryKey: ['leitwarte', 'vorgaenge', filter] });
      const vorher = qc.getQueryData<Vorgang[]>(['leitwarte', 'vorgaenge', filter]);
      if (vorher && filter !== 'alle') qc.setQueryData<Vorgang[]>(['leitwarte', 'vorgaenge', filter], vorher.filter((v) => v.id !== p.id));
      return { vorher };
    },
    onError: (e: Error, _p, ctx) => {
      if (ctx?.vorher) qc.setQueryData(['leitwarte', 'vorgaenge', filter], ctx.vorher);
      toast.error(e.message || 'Das hat nicht geklappt');
    },
    onSuccess: (_v, p) => toast.success(meldung(p.entscheidung, p.wiedervorlage_am)),
    onSettled: () => qc.invalidateQueries({ queryKey: ['leitwarte'] }),
  });

  const vorbereitet = liste.filter((v) => v.artefakt != null).length;
  const n = liste.length;
  const kopfzeile = filter === 'offen'
    ? `${zahl(n)} ${mehrzahl(n, 'Ding braucht', 'Dinge brauchen')} dich, ${zahl(vorbereitet)} davon vorbereitet`
    : `${zahl(n)} ${mehrzahl(n, 'Vorgang', 'Vorgänge')} ${filter === 'spaeter' ? 'auf Wiedervorlage' : filter === 'alle' ? 'insgesamt' : filter}`;

  return (
    <div className="lw-grid lw-g-inhalt" style={{ alignItems: 'start' }}>
      <section className="lw-karte" style={{ padding: '24px 28px', gap: 14 }}>
        <div className="lw-kopf">
          <h1 className="lw-h2" style={{ fontSize: 22 }}>{q.data ? kopfzeile : 'Vorgänge'}</h1>
          {q.data?.length && filter === 'offen' ? <span className="lw-sub">Der Reihe nach, das Wichtigste zuerst.</span> : null}
        </div>
        <div className="lw-tabs">
          {FILTER.map((f) => (
            <button key={f.wert} type="button" className={`lw-tab${filter === f.wert ? ' lw-tab-aktiv' : ''}`} onClick={() => setFilter(f.wert)}>{f.label}</button>
          ))}
        </div>
        {!q.data ? (
          q.isLoading ? <Laden text="Lade die Vorgänge …" /> : <Fehlerzeile error={q.error} retry={() => q.refetch()} />
        ) : liste.length === 0 ? (
          <Leer text={filter === 'offen' ? 'Nichts offen. Der Nachtlauf hat nichts gefunden, was dich braucht.' : 'Hier liegt gerade nichts.'} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {liste.map((v, i) => (
              <VorgangKarte
                key={v.id}
                v={v}
                nr={i + 1}
                fokus={fokusId === v.id}
                aufgeklappt={aufgeklappt === v.id}
                umklappen={() => setAufgeklappt((a) => (a === v.id ? null : v.id))}
                entscheiden={(e, wiedervorlage_am) => mut.mutate({ id: v.id, entscheidung: e, wiedervorlage_am })}
                laeuft={mut.isPending && mut.variables?.id === v.id}
              />
            ))}
          </div>
        )}
      </section>
      <aside className="lw-karte" style={{ gap: 8 }}>
        <h2 className="lw-h2" style={{ fontSize: 13 }}>So funktioniert die Liste</h2>
        <div className="lw-12 lw-sek" style={{ display: 'flex', flexDirection: 'column', gap: 6, lineHeight: 1.5 }}>
          <span><span className="lw-weiss lw-fett">Ja</span> gibt frei. Mailentwürfe und Änderungen führt der nächste Lauf aus, danach steht der Vorgang unter „Freigegeben", bis du ihn als erledigt markierst.</span>
          <span><span className="lw-weiss lw-fett">Später</span> legt eine Wiedervorlage an, Vorgabe in drei Tagen. Der Vorgang taucht dann wieder unter „Offen" auf.</span>
          <span><span className="lw-weiss lw-fett">Nein</span> verwirft. Der Lauf legt den Punkt nicht noch einmal vor.</span>
          <span>Die Farbe am linken Rand ist die Dringlichkeit: rot heute, gelb diese Woche, blau bei Gelegenheit.</span>
        </div>
      </aside>
    </div>
  );
}
